import { createSelector, MemoizedSelector, Selector } from '@ngrx/store';

import { getMetaAdapter, getMetaSelectors } from '../meta-adapter';
import { namedProjector } from '../selector-utils';
import { Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import { getSingleEntityActions } from './actions';
import { getSingleEntityDataAdapter } from './data';
import { SingleEntityAdapter, SingleEntityState } from './types';

const singleEntityInitialState: SingleEntityState<any> = Object.freeze({
  data: null,
  loaded: false,
  pending: false,
  failed: false,
  error: null,
} as const);

export const createSingleEntityAdapter = <T extends object>(
  baseSelector: Selector<any, SingleEntityState<T>>,
  namespace: string,
  references: Reference<T>[] = [],
) => {
  const adapter: SingleEntityAdapter<T> = {
    ...getMetaAdapter<SingleEntityState<T>>(),
    ...getSingleEntityDataAdapter(singleEntityInitialState),
  };

  const defaultActions = getSingleEntityActions<T>(adapter);
  const dataSelector = createSelector(
    baseSelector,
    namedProjector(`${namespace}Data`, state => state.data),
  );
  const dataWithRefsSelector = <MemoizedSelector<any, T | null>>createSelectorWithReferences(
    dataSelector,
    references,
    namedProjector(`${namespace}Data`, data => data),
  );

  const selectors = {
    ...getMetaSelectors(baseSelector, namespace),
    data: references.length ? dataWithRefsSelector : dataSelector,
    state: createSelector(
      baseSelector,
      namedProjector(`${namespace}State`, state => state),
    ),
  };

  return {
    adapter,
    selectors,
    defaultActions,
    initialState: <SingleEntityState<T>>singleEntityInitialState,
  };
};
